import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import AudioRecorder from '../components/AudioRecorder';
import DetectionResults from '../components/DetectionResults';
import TranscriptionDemo from '../components/TranscriptionDemo';
import ModelLoader from '../components/ModelLoader';
import './Pryme.css';
import { useConfig } from '../ConfigContext';
import { getApiBaseUrl } from '../config';

function Pryme() {
  const config = useConfig();
  const pp = config.prymePage ?? {};
  const pv = pp.sections ?? {};
  const showPH = pp.showPlaceholders ?? true; // admin toggle to hide placeholder boxes
  const location = useLocation();

  const [activePipelineStep, setActivePipelineStep] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [animatedScores, setAnimatedScores] = useState({ intent: 0, semantic: 0, fused: 0 });
  const [demoRunning, setDemoRunning] = useState(false);
  const demoInterval = useRef(null);

  // Demo state
  const [keywordInput, setKeywordInput] = useState('hello,goodbye,thank you');
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentLabels, setCurrentLabels] = useState(['hello', 'goodbye']);
  const [detectionResult, setDetectionResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);   // loading animation active
  const [transcriptionReady, setTranscriptionReady] = useState(false); // transcription engine warmed up
  const [error, setError] = useState(null);
  const [activeDemo, setActiveDemo] = useState('detection'); // null = both closed
  const demoSectionRef = useRef(null);

  const runScoreDemo = () => {
    setDemoRunning(true);
    let step = 0;
    const targets = { intent: 0.81, semantic: 0.74, fused: 0.88 };
    demoInterval.current = setInterval(() => {
      step += 1;
      const t = Math.min(step / 30, 1);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setAnimatedScores({
        intent:   parseFloat((targets.intent   * ease).toFixed(2)),
        semantic: parseFloat((targets.semantic * ease).toFixed(2)),
        fused:    parseFloat((targets.fused    * ease).toFixed(2)),
      });
      if (step >= 30) {
        clearInterval(demoInterval.current);
        setDemoRunning(false);
      }
    }, 40);
  };

  const handleConfigSubmit = async (configData) => {
    setModelLoading(true);
    setError(null);
    try {
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configData),
      });
      if (!response.ok) throw new Error('Failed to initialize detector');
      const data = await response.json();
      setCurrentLabels(data.labels);
      setIsInitialized(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setModelLoading(false);
    }
  };

  // Called when user clicks "Start Transcription Engine" button
  const handleWarmupTranscription = async () => {
    setModelLoading(true);
    setError(null);
    try {
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/warmup`, { method: 'POST' });
      if (!response.ok) throw new Error('Warmup failed');
      setTranscriptionReady(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setModelLoading(false);
    }
  };

  const handleAudioUpload = async (audioBlob) => {
    if (!isInitialized) { setError('Please configure keywords first'); return; }
    if (audioBlob.size === 0) { console.warn('Empty audio blob received'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.wav');
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/transcribe`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Transcription failed');
      const data = await response.json();
      if (data.transcript && data.transcript.trim().length > 0) {
      }
      setDetectionResult(data);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToDemo = () => {
    if (demoSectionRef.current) {
      demoSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => { return () => clearInterval(demoInterval.current); }, []);
  useEffect(() => {
    if (location.hash === '#try-pryme-live') {
      const element = document.getElementById('try-pryme-live');
      if (element) element.scrollIntoView({ behavior: 'smooth' });
      else if (demoSectionRef.current) demoSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  // ── CONFIG-DRIVEN DATA ──────────────────────────────────────

  const heroStats = (pp.heroStats ?? []).length > 0
    ? pp.heroStats
    : [
        { value: '0',     label: 'Training samples required' },
        { value: '<50ms', label: 'Detection latency' },
        { value: '3',     label: 'Verification layers' },
      ];

  const capabilities = (pp.capabilities ?? []).length > 0
    ? pp.capabilities.map((c, i) => ({ id: i + 1, title: c.title, description: c.desc, icon: c.icon ?? null }))
    : [
        { id: 1, title: 'No Training Required',             icon: '/icons/pryme/training-data.png',       description: 'Define any keyword or phrase as a plain text label and Pryme recognizes it immediately. No audio samples. No annotation pipeline. No retraining cycle. Detection is live from the first utterance.' },
        { id: 2, title: 'Dual-Engine Confidence',           icon: '/icons/pryme/detection.png',           description: 'Every spoken phrase is evaluated by two independent AI engines simultaneously — one assessing semantic intent with speech-aware framing, one measuring meaning similarity in vector space. Their scores are fused into a single calibrated confidence signal.' },
        { id: 3, title: 'Open Transcription Mode',          icon: '/icons/pryme/open-transcription.png',  description: 'Switch from keyword detection to full open-ended transcription with a single class swap. The same audio pipeline, VAD, and speech model power both modes — giving you accurate transcription without a separate integration.' },
        { id: 4, title: 'Neural Voice Activity Detection',  icon: '/icons/pryme/neural-vad.png',          description: 'A dedicated neural VAD model identifies speech boundaries in real time with configurable silence gating. Non-speech audio never reaches the transcription or detection layers, keeping compute usage low and results clean.' },
        { id: 5, title: 'Edge & Cloud Deployable',          icon: '/icons/pryme/edge-cloud.png',          description: 'The complete Pryme pipeline runs wherever you need it — on embedded hardware and air-gapped devices for maximum privacy, or on cloud and server infrastructure for scalable, high-throughput deployments. Same pipeline, same API, same accuracy.' },
        { id: 6, title: 'Mic & File Input',                 icon: '/icons/pryme/mic-audio-file.png',      description: 'Pryme supports both live microphone streams and pre-recorded audio files out of the box. Switch inputs without changing your detection or transcription logic — the same pipeline handles both.' },
      ];

  // Pipeline steps — reads from admin config if set, falls back to hardcoded defaults
  const PIPELINE_DEFAULTS = [
    { id: 'vad',      number: '01', label: 'Voice Activity Detection', icon: <img src="/icons/pryme/VAD.png" alt="" />,
      detail: 'A dedicated neural VAD model operates continuously on the raw audio stream — isolating speech boundaries, filtering silence, and gating downstream processing. No audio reaches the transcription or detection layers until speech is confirmed.' },
    { id: 'asr',      number: '02', label: 'On-Device Transcription',  icon: <img src="/icons/pryme/Transcription.png" alt="" />,
      detail: 'Each confirmed speech segment is transcribed by a compact, optimized model with no network dependency. The transcript is produced locally and passed directly to the detection layer — or returned as the final result in open transcription mode.' },
    { id: 'intent',   number: '03', label: 'Intent Classification',    icon: <img src="/icons/pryme/Intent.png" alt="" />,
      detail: "Pryme's proprietary intent engine evaluates whether the transcript entails each target keyword — using speech-aware framing that generic classifiers don't apply. All keywords are scored in a single pass, so latency stays flat as your vocabulary grows." },
    { id: 'semantic', number: '04', label: 'Semantic Similarity',      icon: <img src="/icons/pryme/Semantic.png" alt="" />,
      detail: 'A second independent engine encodes both transcript and keyword into a shared meaning space. Paraphrases, synonyms, and natural command variants are recognized as equivalent without any additional configuration.' },
    { id: 'fuse',     number: '05', label: 'Fusion & Decision',        icon: <img src="/icons/pryme/fusion.png" alt="" />,
      detail: 'Intent and semantic scores are fused via a configurable weighted sum and checked against a confidence threshold. An optional lexical guard layer provides a final cross-check against the raw transcript.' },
  ];
  const pipelineSteps = PIPELINE_DEFAULTS.map((def, i) => ({
    ...def,
    label:  pp.pipeline?.[i]?.label  || def.label,
    detail: pp.pipeline?.[i]?.detail || def.detail,
  }));
  const useCases = (pp.useCases ?? []).length > 0
    ? pp.useCases.map((uc, i) => ({
        id: i + 1,
        category: uc.title,
        title: uc.title,
        description: uc.desc,
        features: uc.features ?? [],
      }))
    : [
        { id: 1, category: 'Smart Devices & IoT',       title: 'Custom Wake Word Detection',  description: 'Activate devices with any brand-specific wake phrase — no recordings, no vendor-managed training pipeline, no lock-in. Define the word, deploy the same day.',          features: ['Any wake phrase', 'No audio samples', 'Edge-ready'] },
        { id: 2, category: 'Robotics & Automation',     title: 'Voice-Triggered Commands',    description: 'Issue start, stop, and action commands to automated systems using natural speech. Wake word and shutdown word detection cleanly control session boundaries.',               features: ['Wake + shutdown words', 'Multi-keyword monitoring', 'Noise robust'] },
        { id: 3, category: 'Enterprise & Contact Centers', title: 'Real-Time Intent Detection', description: 'Monitor live interactions for specific phrases, escalation triggers, or compliance keywords — without building a custom model for each use case.',                       features: ['Multi-label detection', 'Live audio stream', 'No retraining'] },
      ];

  const advantages = (pp.advantages ?? []).length > 0
    ? pp.advantages.map(a => ({ value: a.value, metric: a.metric, description: a.desc }))
    : [
        { value: '0',       metric: 'Training Examples Required', description: 'Define any keyword in text — detection is immediate' },
        { value: '3-Layer', metric: 'Verification Architecture',  description: 'Intent · Semantic · Precision Guard' },
        { value: '<50ms',   metric: 'Detection Latency',          description: 'Low-latency pipeline on edge hardware and cloud deployments' },
        { value: 'Live',    metric: 'Runtime Customization',      description: 'Add or change keywords instantly, no redeployment' },
      ];

  const ctaChecklist = (pp.ctaChecklist ?? []).length > 0
    ? pp.ctaChecklist
    : [
        'Zero training examples required',
        'Keyword detection & open transcription',
        'Edge, on-chip, or cloud deployment',
        'Live microphone & audio file support',
        'Deploys the same day',
      ];

  const [activeCompCol, setActiveCompCol] = useState(0);
  const comparison = [
    { feature: 'Training Data',         pryme: 'None — text label only',        traditional: 'Hours of labeled audio',   cloud: 'N/A (fixed vocabulary)' },
    { feature: 'New Keyword Setup',     pryme: 'Instant',                        traditional: 'Days to weeks',            cloud: 'Not supported' },
    { feature: 'Detection Method',      pryme: 'Proprietary multi-stage engine', traditional: 'Template matching',        cloud: 'Grammar / intent rules' },
    { feature: 'Paraphrase Handling',   pryme: 'Handled natively',               traditional: 'Exact match only',         cloud: 'Limited' },
    { feature: 'Open Transcription',    pryme: 'Built-in, same pipeline',        traditional: 'Separate model required',  cloud: 'Separate API / billing' },
    { feature: 'VAD',                   pryme: 'Neural, configurable gating',    traditional: 'Energy-based or none',     cloud: 'Server-side' },
    { feature: 'Deployment',            pryme: 'Edge, on-chip, or cloud',        traditional: 'Typically on-device',      cloud: 'Cloud only' },
    { feature: 'Lexical Guard',         pryme: 'Optional, configurable',         traditional: 'Not available',            cloud: 'Not available' },
    { feature: 'Audio Input',           pryme: 'Mic + audio file',               traditional: 'Mic only (typically)',     cloud: 'Stream or upload' },
    { feature: 'Runtime Customization', pryme: 'Live label swap, no restart',    traditional: 'Requires retraining',      cloud: 'Fixed API' },
  ];

  const heroTitleLines = (pp.heroTitle ?? 'Keyword Detection.\nNo Training.\nNo Limits.')
    .split('\n')
    .map((line, i, arr) => i < arr.length - 1 ? <React.Fragment key={i}>{line}<br /></React.Fragment> : <React.Fragment key={i}>{line}</React.Fragment>);

  return (
    <div className="pryme-page">

      {/* ── HERO ── */}
      {(pv.hero ?? true) && (
        <section className="pryme-hero">
          <div className="pryme-hero-left">
            <div className="hero-badge">{config.branding.zslName}</div>
            <h1 className="hero-title">{heroTitleLines}</h1>
            <p className="hero-subtitle">
              {pp.heroSubtitle ?? 'Pryme detects any spoken keyword or phrase the moment you define it — powered by a three-layer detection architecture that understands meaning, not just sound.'}
            </p>
            <div className="hero-cta-group">
              <button onClick={scrollToDemo} className="cta-button primary" style={{ cursor: 'pointer', border: 'none' }}>
                Try {config.branding.zslName}
              </button>
              <Link to="/download" className="cta-button secondary" style={{ cursor: 'pointer', textDecoration: 'none' }}>
                Download Pryme SDK
              </Link>
            </div>
            <div className="hero-stat-row">
              {heroStats.map((stat, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="hero-stat-divider" />}
                  <div className="hero-stat">
                    <span className="hero-stat-value">{stat.value}</span>
                    <span className="hero-stat-label">{stat.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="pryme-hero-right">
            {pp.heroImage
              ? <img src={pp.heroImage} alt="Pryme Hero Visual" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'8px', opacity: 0.25 }} />
              : showPH && <div className="visual-placeholder-box pryme-hero-visual-box">
                  <span className="visual-placeholder-label">Hero Visual</span>
                  <span className="visual-placeholder-sub">Product demo video / waveform animation</span>
                </div>
            }
          </div>
        </section>
      )}

      {/* ── CORE INSIGHT ── */}
      <section className="core-insight-section">
        <div className="insight-text">
          <h2>A Different Approach to Keyword Detection</h2>
          <p>
            Conventional keyword engines match audio to rigid pre-recorded templates.
            They require hundreds of voice samples per keyword, dedicated training pipelines,
            and a full redeployment cycle every time your vocabulary changes.
          </p>
          <p>
            <strong>Pryme doesn’t force a tradeoff between meaning and precision.</strong> Detect by context using deep language intelligence or 
            enforce exact word matching, no keyword-specific training at any stage.
          </p>
        </div>
        {pp.comparisonImage
          ? <img src={pp.comparisonImage} alt="Pryme Comparison" style={{ width:'100%', borderRadius:'8px', marginTop:'2rem' }} />
          : showPH && <div className="visual-placeholder-box wide">
              <span className="visual-placeholder-label">Comparison Graphic</span>
              <span className="visual-placeholder-sub">Traditional pipeline vs. Pryme — side by side</span>
            </div>
        }
      </section>

      {/* ── DEMO SECTION ── */}
      <section className="pryme-demo-section" id="try-pryme-live" ref={demoSectionRef}>
        <div className="demo-accordions-header">
          <h2>Try {config.branding.zslName} Live</h2>
          <p className="demo-accordions-sub">Keyword detection and open transcription, both powered by the same pipeline.</p>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {/* ── Detect accordion ── */}
        <div className={`demo-accordion ${activeDemo === 'detection' ? 'demo-accordion-open' : ''}`}>
          <button
            className="demo-accordion-trigger"
            onClick={() => setActiveDemo(activeDemo === 'detection' ? null : 'detection')}
            aria-expanded={activeDemo === 'detection'}
          >
            <div className="demo-accordion-trigger-left">
              <span className="demo-accordion-icon">⬡</span>
              <div>
                <span className="demo-accordion-title">Keyword Detection</span>
                <span className="demo-accordion-subtitle">Define keywords in plain text — detect them in any sentence</span>
              </div>
            </div>
            <span className="demo-accordion-chevron">{activeDemo === 'detection' ? '▲' : '▼'}</span>
          </button>

          {activeDemo === 'detection' && (
            <div className="demo-accordion-body">
              <div className="demo-accordion-inner">
                <div className="demo-accordion-desc">
                  <ol>
                    <li>Type any keyword or phrase</li>
                    <li>Apply your keywords</li>
                    <li>Start speaking</li>
                  </ol>
                  {isInitialized && (
                    <div className="demo-active-labels">
                      <span className="demo-active-labels-title">Listening for</span>
                      <div className="demo-label-chips">
                        {currentLabels.map(l => <span key={l} className="demo-label-chip">{l}</span>)}
                      </div>
                      <button className="demo-reset-btn" onClick={() => { setIsInitialized(false); setDetectionResult(null); }}>
                        Change keywords
                      </button>
                    </div>
                  )}
                </div>

                <div className="demo-accordion-interactive">
                  {!isInitialized && !modelLoading && config.demoPage.configPanel && (
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const labelArray = keywordInput.split(',').map(l => l.trim()).filter(l => l.length > 0);
                      if (labelArray.length === 0) { alert('Please enter at least one keyword'); return; }
                      handleConfigSubmit({
                        labels: labelArray,
                        fused_threshold: config.demoPage.detectionSettings.fused_threshold,
                        w_nli: config.demoPage.detectionSettings.w_nli,
                        lexical_guard: config.demoPage.detectionSettings.lexical_guard,
                      });
                    }} className="config-panel">
                      <div className="form-group">
                        <label htmlFor="labels">Enter Keywords</label>
                        <input
                          id="labels"
                          type="text"
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          placeholder="e.g., hello, goodbye, thank you"
                        />
                        <small>Separate keywords with commas</small>
                      </div>
                      <button type="submit" className="button">Apply Keywords</button>
                    </form>
                  )}
                  {modelLoading && <ModelLoader mode="detection" />}
                  {isInitialized && !modelLoading && config.demoPage.audioRecorder && (
                    <AudioRecorder
                      onAudioReady={handleAudioUpload}
                      isLoading={isLoading}
                    />
                  )}
                  {detectionResult && !modelLoading && config.demoPage.detectionResults && (
                    <DetectionResults result={detectionResult} />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Transcribe accordion ── */}
        {config.demoPage.transcriptionDemo && (
          <div className={`demo-accordion ${activeDemo === 'transcription' ? 'demo-accordion-open' : ''}`}>
            <button
              className="demo-accordion-trigger"
              onClick={() => setActiveDemo(activeDemo === 'transcription' ? null : 'transcription')}
              aria-expanded={activeDemo === 'transcription'}
            >
              <div className="demo-accordion-trigger-left">
                <span className="demo-accordion-icon">⬡</span>
                <div>
                  <span className="demo-accordion-title">Open Transcription</span>
                  <span className="demo-accordion-subtitle">Speak freely — get accurate text back in real time</span>
                </div>
              </div>
              <span className="demo-accordion-chevron">{activeDemo === 'transcription' ? '▲' : '▼'}</span>
            </button>

            {activeDemo === 'transcription' && (
              <div className="demo-accordion-body">
                <div className="demo-accordion-inner">
                  <div className="demo-accordion-desc">
                    <ol>
                      <li>Click the microphone</li>
                      <li>Speak naturally</li>
                      <li>Transcriptions appear in real time</li>
                    </ol>
                  </div>
                  <div className="demo-accordion-interactive">
                    {!transcriptionReady && !modelLoading && (
                      <div className="transcription-gate">
                        <p className="transcription-gate-desc">
                          Loads once per session — stays ready after that.
                        </p>
                        <button className="demo-start-btn" onClick={handleWarmupTranscription}>
                          Start Transcription Engine
                        </button>
                      </div>
                    )}
                    {modelLoading && <ModelLoader mode="transcription" />}
                    {transcriptionReady && !modelLoading && (
                      <TranscriptionDemo isLoading={isLoading} />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── PIPELINE + TWO MODES ── */}
      <div className="pipeline-modes-band">

        {/* ── PIPELINE ── */}
        {(pv.pipeline ?? true) && (
          <section className="pipeline-section" id="how-it-works">
            <div className="section-header">
              <h2>The {config.branding.zslName} Pipeline</h2>
              <p className="section-subtitle">
                Five stages from raw audio to a verified detection event.
                Tap any stage to learn more.
              </p>
            </div>

            <div className="pipeline-container">
              {pipelineSteps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <div
                    className={`pipeline-step ${activePipelineStep === step.id ? 'active' : ''}`}
                    onClick={() => setActivePipelineStep(activePipelineStep === step.id ? null : step.id)}
                  >
                    <div className="pipeline-step-header">
                      <span className="pipeline-step-number">{step.number}</span>
                      <h3 className="pipeline-step-label">{step.label}</h3>
                    </div>
                    <div className="pipeline-step-icon">{step.icon}</div>
                    {activePipelineStep === step.id && (
                      <div className="pipeline-step-detail"><p>{step.detail}</p></div>
                    )}
                  </div>
                  {idx < pipelineSteps.length - 1 && (
                    <div className="pipeline-connector">→</div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Confidence Fusion Visualizer */}
            <div className="score-visualizer">
              <div className="score-visualizer-header">
                <h3>Confidence Fusion — Live Example</h3>
                <p>Transcript: "cut the power" &nbsp;·&nbsp; Target keyword: "turn off"</p>
                <button className="score-demo-button" onClick={runScoreDemo} disabled={demoRunning}>
                  {demoRunning ? 'Analyzing…' : 'Run Example'}
                </button>
              </div>
              <div className="score-bars">
                <div className="score-bar-row">
                  <span className="score-bar-label">Intent Score</span>
                  <div className="score-bar-track">
                    <div className="score-bar-fill intent" style={{ width: `${animatedScores.intent * 100}%` }} />
                  </div>
                  <span className="score-bar-value">{animatedScores.intent.toFixed(2)}</span>
                </div>
                <div className="score-bar-row">
                  <span className="score-bar-label">Semantic Score</span>
                  <div className="score-bar-track">
                    <div className="score-bar-fill sim" style={{ width: `${animatedScores.semantic * 100}%` }} />
                  </div>
                  <span className="score-bar-value">{animatedScores.semantic.toFixed(2)}</span>
                </div>
                <div className="score-bar-row fused-row">
                  <span className="score-bar-label">Fused Confidence</span>
                  <div className="score-bar-track">
                    <div className="score-bar-fill fused" style={{ width: `${animatedScores.fused * 100}%` }} />
                  </div>
                  <span className="score-bar-value">{animatedScores.fused.toFixed(2)}</span>
                </div>
              </div>
              <p className="score-visualizer-note">
                Fused confidence clears threshold — <strong>DETECTED</strong>
              </p>
            </div>

            {pp.pipelineImage
              ? <img src={pp.pipelineImage} alt="Pryme Pipeline" style={{ width:'100%', borderRadius:'8px', marginTop:'3rem' }} />
              : showPH && <div className="visual-placeholder-box wide" style={{ marginTop:'3rem' }}>
                  <span className="visual-placeholder-label">Pipeline Diagram</span>
                  <span className="visual-placeholder-sub">Architecture illustration — multi-stage detection pipeline</span>
                </div>
            }
          </section>
        )}

        {/* ── TWO MODES ── */}
        <section className="two-modes-section">
          <div className="section-header">
            <h2>One SDK. Two Modes.</h2>
            <p className="section-subtitle">
              {config.branding.zslName} ships with two ready-to-use processor classes, switch between them
              based on what your application needs, with no pipeline changes required.
            </p>
          </div>
          <div className="two-modes-grid">
            <div className="mode-card">
              <div className="mode-card-header">
                <span className="mode-label">Keyword Detection</span>
                <h3>{config.branding.zslName}KeywordDetector</h3>
              </div>
              {showPH && <div className="visual-placeholder-box mode-visual">
                <span className="visual-placeholder-label">Visual</span>
                <span className="visual-placeholder-sub">Keyword detection diagram</span>
              </div>}
              <p className="mode-description">
                Listens for any set of keywords or phrases you define in plain text.
                Each utterance is transcribed, then evaluated by the dual-engine
                intent and semantic pipeline. Returns a detection result, per-label
                confidence scores, and the top matching label for every audio segment.
              </p>
              <ul className="mode-features">
                <li>Multi-label keyword vocabulary</li>
                <li>Configurable confidence threshold</li>
                <li>Optional lexical guard layer</li>
                <li>Speech-aware hypothesis framing</li>
              </ul>
            </div>

            <div className="mode-card mode-card-alt">
              <div className="mode-card-header">
                <span className="mode-label">Open Transcription</span>
                <h3>{config.branding.zslName}Transcriber</h3>
              </div>
              {showPH && <div className="visual-placeholder-box mode-visual">
                <span className="visual-placeholder-label">Visual</span>
                <span className="visual-placeholder-sub">Transcription output diagram</span>
              </div>}
              <p className="mode-description">
                Transcribes open-ended speech without any keyword vocabulary.
                The same VAD pipeline and speech model are used — only the classification
                layer is bypassed. Ideal for dictation, logging, meeting capture,
                or anywhere you need raw, accurate text from spoken audio.
              </p>
              <ul className="mode-features">
                <li>Continuous or utterance-based transcription</li>
                <li>Configurable language and model size</li>
                <li>No keyword vocabulary required</li>
                <li>Same mic and file input support</li>
              </ul>
            </div>
          </div>
        </section>

      </div>{/* end pipeline-modes-band */}

      {/* ── CAPABILITIES ── */}
      {(pv.capabilities ?? true) && (
        <section className="capabilities-section" id="capabilities">
          <div className="section-header">
            <h2>Core Capabilities</h2>
            <p className="section-subtitle">
              Every capability is a direct product of the three-layer detection architecture
            </p>
          </div>
          <div className="capabilities-grid">
            {capabilities.map((cap) => (
              <div key={cap.id} className="capability-card">
                <div className="capability-icon-wrapper">
                  {cap.icon && <img src={cap.icon} alt={cap.title} className="capability-icon-img" />}
                </div>
                <h3>{cap.title}</h3>
                <p>{cap.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── ADVANTAGES ── */}
      {(pv.advantages ?? true) && (
        <section className="advantages-section">
          <div className="section-header">
            <h2>What Makes {config.branding.zslName} Different</h2>
            <p className="section-subtitle">
              The metrics that define a training-free detection architecture
            </p>
          </div>
          <div className="advantages-grid">
            {advantages.map((adv, idx) => (
              <div key={idx} className="advantage-card">
                <div className="advantage-value">{adv.value}</div>
                <div className="advantage-metric">{adv.metric}</div>
                <div className="advantage-description">{adv.description}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── USE CASES ── */}
      {(pv.useCases ?? true) && (
        <section className="use-cases-section">
          <div className="section-header">
            <h2>Real-World Applications</h2>
            <p className="section-subtitle">
              Any domain that needs voice triggers without building a custom model
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((uc) => (
              <div
                key={uc.id}
                className={`use-case-card ${expandedCard === uc.id ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === uc.id ? null : uc.id)}
              >
                <div className="use-case-header">
                  <div className="use-case-category">{uc.category}</div>
                  <h3>{uc.title}</h3>
                </div>
                <p className="use-case-description">{uc.description}</p>
                <div className="use-case-features">
                  {uc.features.map((f, i) => (
                    <span key={i} className="feature-tag">{f}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {showPH && <div className="visual-placeholder-box wide" style={{ marginTop: '3rem' }}>
            <span className="visual-placeholder-label">Use Case Visuals</span>
            <span className="visual-placeholder-sub">Device mockups / industry scenario illustrations</span>
          </div>}
        </section>
      )}

      {/* ── COMPARISON TABLE ── */}
      {(pv.comparison ?? true) && (
        <section className="tech-stack-section">
          <div className="section-header">
            <h2>{config.branding.zslName} vs. Traditional Approaches</h2>
            <p className="section-subtitle">
              How meaning-aware detection changes the keyword detection landscape
            </p>
          </div>
          {/* Desktop */}
          <div className="comparison-table comparison-desktop-only">
            <div className="comparison-row header">
              <div className="comparison-cell feature">Feature</div>
              <div className="comparison-cell">{config.branding.zslName}</div>
              <div className="comparison-cell">Traditional ASR</div>
              <div className="comparison-cell">Cloud Voice APIs</div>
            </div>
            {comparison.map((row, idx) => (
              <div key={idx} className="comparison-row">
                <div className="comparison-cell feature">{row.feature}</div>
                <div className="comparison-cell highlight">{row.pryme}</div>
                <div className="comparison-cell">{row.traditional}</div>
                <div className="comparison-cell">{row.cloud}</div>
              </div>
            ))}
          </div>
          {/* Mobile */}
          <div className="comp-mobile">
            <div className="comp-mobile-tabs">
              {['Traditional ASR', 'Cloud Voice APIs'].map((label, i) => (
                <button key={i} className={`comp-mobile-tab${activeCompCol === i ? ' active' : ''}`} onClick={() => setActiveCompCol(i)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="comp-mobile-header-row">
              <div className="comp-mobile-th comp-mobile-feature-th">Feature</div>
              <div className="comp-mobile-th comp-mobile-hero-th">{config.branding.zslName}</div>
              <div className="comp-mobile-th">{activeCompCol === 0 ? 'Traditional ASR' : 'Cloud Voice APIs'}</div>
            </div>
            {comparison.map((row, idx) => (
              <div key={idx} className="comp-mobile-row">
                <div className="comp-mobile-feature">{row.feature}</div>
                <div className="comp-mobile-val comp-mobile-val-hero">{row.pryme}</div>
                <div className="comp-mobile-val">{activeCompCol === 0 ? row.traditional : row.cloud}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── PRIVACY CALLOUT ── */}
      {(pv.privacy ?? true) && (
        <section className="privacy-section">
          <div className="privacy-box">
            <img src="/icons/general/privacy.png" alt="Privacy" className="privacy-icon-img" />
            <div className="privacy-content">
              <h3>Audio Privacy You Control</h3>
              <p>
                {config.branding.zslName} is designed to run wherever your privacy requirements demand.
                On embedded hardware and air-gapped deployments, the complete pipeline —
                voice activity detection, transcription, intent classification, semantic
                analysis, and decision logic — runs entirely locally with no audio leaving
                the device. For cloud and server deployments, you control the infrastructure.
                Either way, {config.branding.zslName} imposes no external data dependency by default.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="pryme-cta-section">
        <div className="cta-split">
          <div className="cta-left">
            <p className="cta-eyebrow">Get started with</p>
            <h2 className="cta-heading">
              <span className="cta-brand">{config.branding.zslName.toUpperCase()}</span>
              <span className="cta-descriptor">Keyword Detection</span>
            </h2>
            <p className="cta-body">
              {pp.ctaBody ?? 'Define any keyword in plain text and deploy the same day — on embedded hardware, in the cloud, or anywhere in between.'}
            </p>
            <a
              href={config.navbar.contactUs.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button primary large cta-demo-btn"
            >
              Contact Us
            </a>
          </div>
          <div className="cta-right">
           <ul className="cta-checklist">
              {ctaChecklist.map((item, i) => (
                <li key={i} className="cta-check-item">
                  <span className="cta-check-icon">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Pryme;